import { useState, useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useTables } from '../../hooks/useDbDictionary.js';
import { useAuth } from '../../hooks/useAuth.js';
import { dbDictionaryApi } from '../../lib/api.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';
import { TableDetailModal } from '../../components/features/db-dictionary/index.js';
import type { TableFilter } from '../../types/db-dictionary.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書テーブル一覧ページ。スキーマ内テーブルの検索・フィルタ・詳細モーダル表示
 * @graph-connects useTables [calls] テーブル一覧フック呼び出し
 * @graph-connects TableDetailModal [calls] テーブル詳細モーダル表示
 */
export const Route = createFileRoute('/db-dictionary/$schema/')({
  component: DbDictionaryTableListPage,
});

/**
 * フィルタチップの定義
 * @graph-connects none
 */
const FILTER_OPTIONS: TableFilter[] = ['all', 'checked', 'unchecked', 'hasDead'];

/**
 * テーブル一覧ページ
 * @graph-connects none
 */
function DbDictionaryTableListPage() {
  const { t } = useTranslation();
  const { schema } = Route.useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<TableFilter>('all');
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');
  const { data, isLoading, isError, refetch } = useTables(schema, activeFilter);
  const tables = data?.tables;
  const schemaInfo = data?.schema;
  const [editingSchemaDesc, setEditingSchemaDesc] = useState(false);
  const [schemaDescDraft, setSchemaDescDraft] = useState('');

  // クライアント側での検索フィルタ
  const filteredTables = useMemo(() => {
    if (!tables) return [];
    if (!searchQuery) return tables;
    const q = searchQuery.toLowerCase();
    return tables.filter((t) => t.name.toLowerCase().includes(q));
  }, [tables, searchQuery]);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{schema}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('dbDictionary.tables')}</p>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{schema}</h1>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダーとナビゲーション */}
      <div className="flex flex-col gap-4 animate-content-reveal">
        <Link
          to="/db-dictionary"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('dbDictionary.actions.backToSchemas')}
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{schema}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">
            {t('dbDictionary.tables')}
            {tables && ` (${String(filteredTables.length)})`}
          </p>
        </div>
      </div>

      {/* スキーマ説明文 */}
      {schemaInfo && (
        <Card className="animate-content-reveal">
          <CardContent className="py-3">
            {canEdit && editingSchemaDesc ? (
              <div className="flex gap-2">
                <textarea
                  className="flex-1 bg-[var(--color-bg-base)] text-[var(--color-text-primary)] border border-[var(--color-border)] rounded px-3 py-2 text-sm resize-none"
                  rows={3}
                  value={schemaDescDraft}
                  onChange={(e) => {
                    setSchemaDescDraft(e.target.value);
                  }}
                />
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      void (async () => {
                        await dbDictionaryApi.updateSchemaDescription(schema, {
                          humanDescription: schemaDescDraft,
                        });
                        setEditingSchemaDesc(false);
                        void refetch();
                      })();
                    }}
                  >
                    {t('dbDictionary.actions.save')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingSchemaDesc(false);
                    }}
                  >
                    {t('dbDictionary.actions.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className={`text-sm text-[var(--color-text-secondary)] ${canEdit ? 'cursor-pointer hover:text-[var(--color-text-primary)]' : ''} transition-colors`}
                onClick={
                  canEdit
                    ? () => {
                        setSchemaDescDraft(
                          schemaInfo.humanDescription ?? schemaInfo.description ?? '',
                        );
                        setEditingSchemaDesc(true);
                      }
                    : undefined
                }
              >
                {schemaInfo.humanDescription ??
                  schemaInfo.description ??
                  t('dbDictionary.noDescription')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 検索バーとフィルタチップ */}
      <div className="flex flex-col sm:flex-row gap-3 animate-content-reveal">
        <div className="flex-1">
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            placeholder={t('dbDictionary.search.placeholder')}
            size="sm"
            leftIcon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            }
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTER_OPTIONS.map((filter) => (
            <Button
              key={filter}
              variant={activeFilter === filter ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setActiveFilter(filter);
              }}
            >
              {t(`dbDictionary.filter.${filter}`)}
            </Button>
          ))}
        </div>
      </div>

      {/* テーブル一覧 */}
      {filteredTables.length === 0 ? (
        <Card className="animate-content-reveal">
          <CardContent>
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('dbDictionary.table.noTables')}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="animate-content-reveal">
          <CardHeader title={t('dbDictionary.tables')} />
          <CardContent>
            {/* モバイル: カードビュー */}
            <div className="block md:hidden space-y-3">
              {filteredTables.map((table) => (
                <div
                  key={table.qualifiedName}
                  onClick={() => {
                    setSelectedTable(table.name);
                  }}
                  className="block p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-hover)] transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="font-medium text-[var(--color-text-primary)] truncate">
                      {table.name}
                    </span>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {table.isDead && (
                        <Badge variant="error" size="sm">
                          {t('dbDictionary.table.dead')}
                        </Badge>
                      )}
                      {table.isChecked ? (
                        <Badge variant="success" size="sm">
                          {t('dbDictionary.table.checked')}
                        </Badge>
                      ) : (
                        <Badge variant="default" size="sm">
                          {t('dbDictionary.table.unchecked')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.columnCount')}
                      </p>
                      <p className="text-[var(--color-text-primary)]">{table.columnCount}</p>
                    </div>
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.deadColumnCount')}
                      </p>
                      <p className="text-[var(--color-text-primary)]">
                        {table.deadColumnCount > 0 ? (
                          <span className="text-[var(--color-error)]">{table.deadColumnCount}</span>
                        ) : (
                          '0'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* デスクトップ: テーブルビュー */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('dbDictionary.table.name')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('dbDictionary.table.columns')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('dbDictionary.table.deadColumns')}
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('dbDictionary.table.checked')}
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('dbDictionary.table.dead')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTables.map((table) => (
                    <tr
                      key={table.qualifiedName}
                      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedTable(table.name);
                      }}
                    >
                      <td className="py-3 px-4 text-sm font-medium text-[var(--color-text-primary)]">
                        {table.name}
                      </td>
                      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] text-right">
                        {table.columnCount}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">
                        {table.deadColumnCount > 0 ? (
                          <Badge variant="error" size="sm">
                            {table.deadColumnCount}
                          </Badge>
                        ) : (
                          <span className="text-[var(--color-text-secondary)]">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {table.isChecked ? (
                          <svg
                            className="w-5 h-5 mx-auto text-[var(--color-success)]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <span className="w-5 h-5 mx-auto block rounded-full border-2 border-[var(--color-border)]" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {table.isDead && (
                          <Badge variant="error" size="sm">
                            {t('dbDictionary.table.dead')}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {/* テーブル詳細モーダル */}
      {selectedTable && (
        <TableDetailModal
          schema={schema}
          tableName={selectedTable}
          onClose={() => {
            setSelectedTable(null);
          }}
        />
      )}
    </div>
  );
}
